import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import authAxios from '../utils/authAxios';
import { Button, ConfirmDelete } from '../styles/TableStyles';
import StandardTableLayout from '../styles/StandardTableLayout';
import SizeOptionsModal from '../components/SizeOptionsModal';

const columns = [
  { key: 'Commodity', label: 'Commodity', center: true },
  { key: 'SizeOptions', label: 'Size Options' },
];

const CommodityName = styled.div`
  font-weight: 700;
  font-size: 0.98rem;
`;

const CountSub = styled.div`
  font-size: 0.72rem;
  color: var(--ink-a60);
  font-weight: 500;
  margin-top: 3px;
`;

const SizesCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 0; /* roomier rows so commodity groups breathe */
`;

const SubRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
`;

const SubLabel = styled.span`
  flex: 0 0 64px;
  font-size: 0.64rem;
  font-weight: 800;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: ${({ $origin }) => ($origin === 'import' ? 'var(--black)' : 'var(--green-700)')};
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Chip = styled.span`
  font-size: 0.82rem;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 7px;
  font-variant-numeric: tabular-nums;
  border: 1px solid ${({ $origin }) => ($origin === 'import' ? 'var(--orange-a60)' : 'var(--green-a60)')};
  background: ${({ $origin }) => ($origin === 'import' ? 'var(--orange-a30)' : 'var(--green-a15)')};
  color: ${({ $origin }) => ($origin === 'import' ? 'var(--black)' : 'var(--green-800)')};
`;

const ActionsWrap = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
`;

const SizeOptionsPage = () => {
  const [groups, setGroups] = useState([]);          // [{ UniqueID, CommodityUniqueID, Commodity, domestic[], import[] }]
  const [commodities, setCommodities] = useState([]); // [{ UniqueID, Commodity }]
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState({ open: false, mode: 'add', group: null });
  const [busy, setBusy] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [sizesRes, commRes] = await Promise.all([
        authAxios.get('/api/sizeoptions'),
        authAxios.get('/api/commodities'),
      ]);

      // The API returns rows ordered Commodity → Domestic-before-Import → SortOrder,
      // so pushing per origin preserves the configured order. Orphan rows with no
      // commodity (NULL CommodityUniqueID) are skipped — they are unreachable here.
      const byCommodity = new Map();
      (sizesRes.data || []).forEach((r) => {
        const cid = Number(r.CommodityUniqueID);
        if (!Number.isFinite(cid) || cid <= 0) return;
        if (!byCommodity.has(cid)) {
          byCommodity.set(cid, { CommodityUniqueID: cid, Commodity: r.Commodity || '', domestic: [], import: [] });
        }
        const g = byCommodity.get(cid);
        if (!g.Commodity && r.Commodity) g.Commodity = r.Commodity;
        const origin = String(r.OriginType || 'Domestic') === 'Import' ? 'import' : 'domestic';
        if (r.SizeOption) g[origin].push(r.SizeOption);
      });

      const arr = Array.from(byCommodity.values())
        .map((g) => ({ ...g, UniqueID: g.CommodityUniqueID }))
        .sort((a, b) => String(a.Commodity).localeCompare(String(b.Commodity)));

      setGroups(arr);
      setCommodities((commRes.data || []).map((c) => ({ UniqueID: c.UniqueID, Commodity: c.Commodity })));
    } catch (e) {
      console.error('Error fetching size options:', e);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Add is for commodities without a size entry yet; existing ones are edited.
  const addableCommodities = useMemo(() => {
    const have = new Set(groups.map((g) => Number(g.CommodityUniqueID)));
    return commodities.filter((c) => !have.has(Number(c.UniqueID)));
  }, [groups, commodities]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter((g) =>
      String(g.Commodity).toLowerCase().includes(q) ||
      g.domestic.some((s) => s.toLowerCase().includes(q)) ||
      g.import.some((s) => s.toLowerCase().includes(q))
    );
  }, [groups, search]);

  const openAdd = () => setModal({ open: true, mode: 'add', group: null });
  const openEdit = (group) => setModal({ open: true, mode: 'edit', group });
  const closeModal = () => setModal((m) => ({ ...m, open: false }));

  const handleSaved = async () => {
    closeModal();
    await fetchData();
  };

  // Deleting a commodity's size entry = reconcile to empty (removes every row for it).
  const handleDelete = async (group) => {
    if (!group || busy) return;
    setBusy(true);
    try {
      await authAxios.put(`/api/sizeoptions/commodity/${group.CommodityUniqueID}`, {
        commodity: group.Commodity,
        domestic: [],
        import: [],
      });
      closeModal();
      await fetchData();
    } catch (e) {
      console.error('Error deleting size options:', e);
      alert('Could not delete this commodity’s size options.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <StandardTableLayout
        title="Size Options"
        showForm={false}
        submitting={busy}
        onToggleForm={openAdd}
        onSubmit={(e) => e.preventDefault()}
        columns={columns}
        formData={{}}
        handleChange={() => {}}
        rows={filtered}
        editingId={null}
        searchValue={search}
        onSearchChange={(e) => setSearch(e.target.value)}
        renderCell={(col, row) => {
          if (col === 'Commodity') {
            const total = row.domestic.length + row.import.length;
            return (
              <div>
                <CommodityName>{row.Commodity}</CommodityName>
                <CountSub>{total} size{total === 1 ? '' : 's'}</CountSub>
              </div>
            );
          }
          if (col === 'SizeOptions') {
            if (row.import.length === 0) {
              return (
                <SizesCell>
                  <Chips>
                    {row.domestic.map((s, i) => <Chip key={`d-${i}`} $origin="domestic">{s}</Chip>)}
                  </Chips>
                </SizesCell>
              );
            }
            return (
              <SizesCell>
                <SubRow>
                  <SubLabel $origin="domestic">Domestic</SubLabel>
                  <Chips>
                    {row.domestic.map((s, i) => <Chip key={`d-${i}`} $origin="domestic">{s}</Chip>)}
                  </Chips>
                </SubRow>
                <SubRow>
                  <SubLabel $origin="import">Import</SubLabel>
                  <Chips>
                    {row.import.map((s, i) => <Chip key={`i-${i}`} $origin="import">{s}</Chip>)}
                  </Chips>
                </SubRow>
              </SizesCell>
            );
          }
          return null;
        }}
        renderActions={(row) => (
          <ActionsWrap>
            <Button type="button" onClick={() => openEdit(row)} disabled={busy}>Edit</Button>
            <ConfirmDelete onConfirm={() => handleDelete(row)} disabled={busy} />
          </ActionsWrap>
        )}
      />

      <SizeOptionsModal
        open={modal.open}
        mode={modal.mode}
        commodity={modal.group ? { UniqueID: modal.group.CommodityUniqueID, Commodity: modal.group.Commodity } : null}
        initialDomestic={modal.group ? modal.group.domestic : []}
        initialImport={modal.group ? modal.group.import : []}
        commodities={addableCommodities}
        busy={busy}
        onClose={closeModal}
        onSaved={handleSaved}
        onRequestDelete={() => handleDelete(modal.group)}
      />
    </>
  );
};

export default SizeOptionsPage;
